import React, { useState } from 'react';
import { X, MessageSquare, Check, AlertCircle } from 'lucide-react';
import { submitFeedback } from '../services/api';

const CATEGORIES = [
  { id: 'bug', label: 'Bug', tone: 'rose' },
  { id: 'idea', label: 'Idea', tone: 'emerald' },
  { id: 'missing', label: 'Missing startup', tone: 'amber' },
  { id: 'general', label: 'General', tone: 'slate' },
];

const FeedbackModal = ({ open, onClose }) => {
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const reset = () => {
    setCategory('idea'); setMessage(''); setEmail('');
    setBusy(false); setSent(false); setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 2) {
      setError('Please write a short message.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await submitFeedback({
        message: message.trim(),
        email: email.trim() || undefined,
        category,
        page: window.location.pathname,
      });
      setSent(true);
      setTimeout(handleClose, 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send feedback. Try again?';
      setError(typeof msg === 'string' ? msg : 'Submission failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <MessageSquare size={15} className="text-emerald-600" />
            <h3 className="font-semibold text-slate-900 text-[15px]">Share feedback</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100"
          >
            <X size={15} />
          </button>
        </div>

        {sent ? (
          <div className="px-5 py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center">
              <Check size={22} strokeWidth={3} />
            </div>
            <p className="mt-3 text-slate-900 font-medium">Thanks! We read every note.</p>
            <p className="text-[13px] text-slate-500">It made it to our admin queue.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <div>
              <span className="block text-[12px] font-medium text-slate-700 mb-1.5">Topic</span>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={`text-[12px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                      category === c.id
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="block text-[12px] font-medium text-slate-700 mb-1">Your feedback</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder={
                  category === 'missing'
                    ? 'Which Portland startup should we add? Name + website is enough.'
                    : category === 'bug'
                      ? 'What happened? Steps to reproduce help.'
                      : 'What would make this map better?'
                }
                className="w-full text-[13px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 resize-none"
                autoFocus
              />
              <span className="text-[10.5px] text-slate-400 mt-1 block">{message.length} / 4000</span>
            </label>

            <label className="block">
              <span className="block text-[12px] font-medium text-slate-700 mb-1">
                Your email <span className="text-slate-400 font-normal">(optional, if you want a reply)</span>
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full text-[13px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
              />
            </label>

            {error && (
              <div className="flex items-start gap-2 text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-2">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full mt-1 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-medium text-[14px] py-2.5 rounded-lg transition-colors"
            >
              {busy ? 'Sending…' : 'Send feedback'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
